import { CommerceAPI } from "commerce-api"
import { RootStore } from "./RootStore"
import { Status, StatusMap } from "../types/commerce"
import { action, flow, makeObservable, observable } from "mobx"
import { StoreModel } from "./utils"
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { RetailMediaStoreInitialState } from "../types/store/retailMedia"
import { RetailMediaResponse, SponsoredProductsConfig, RequestBody } from '../types/retail-media'
import { isServer } from '../utils/utils'
import { randomUUID } from 'crypto'
export class RetailMediaStore implements StoreModel {
  api: CommerceAPI
  categoryPageSponsoredProducts: SponsoredProductsConfig | null
  rootStore: RootStore
  retailMediaCatalogID: string
  retailMediaContentById:Record<string, any>
  retailMediaContentStandardID: string
  retailMediaSessionID?: string | null
  retailMediaSponsoredProductsById: Record<string, any>
  retailMediaUrl: string
  searchPageSponsoredProducts: SponsoredProductsConfig | null
  sponsoredProductsConfig: Record<string, SponsoredProductsConfig>
  status: Status

  constructor(rootStore: RootStore, initialState: RetailMediaStoreInitialState) {
    this.api = rootStore.api
    this.rootStore = rootStore
    this.status = initialState?.status ?? StatusMap.INIT
    this.searchPageSponsoredProducts = null
    this.categoryPageSponsoredProducts = null
    this.retailMediaSessionID = initialState?.retailMediaSessionID || this.getOrSetRMSessionId()
    this.retailMediaContentById = initialState?.retailMediaContentById || {}
    this.retailMediaCatalogID = initialState?.retailMediaCatalogID || ''
    this.retailMediaContentStandardID = initialState?.retailMediaContentStandardID || ''
    this.retailMediaUrl = initialState?.retailMediaUrl || ''
    this.retailMediaSponsoredProductsById = initialState?.retailMediaSponsoredProductsById || {}
    this.sponsoredProductsConfig = {}

    makeObservable(this, {
      categoryPageSponsoredProducts: observable,
      retailMediaCatalogID: observable,
      retailMediaContentById: observable,
      retailMediaContentStandardID: observable,
      retailMediaSessionID: observable,
      retailMediaSponsoredProductsById: observable,
      retailMediaUrl: observable,
      searchPageSponsoredProducts: observable,
      sponsoredProductsConfig: observable,
      status: observable,
      setStatus: action.bound,
      fetchBannerAds: flow.bound,
      fetchProductAds: flow.bound,
      getSponsoredProductsConfig: flow.bound,
    })
  }

  get asJson() {
    return {
      categoryPageSponsoredProducts: this.categoryPageSponsoredProducts,
      retailMediaCatalogID: this.retailMediaCatalogID,
      retailMediaContentStandardID: this.retailMediaContentStandardID,
      retailMediaUrl: this.retailMediaUrl,
      searchPageSponsoredProducts: this.searchPageSponsoredProducts,
      sponsoredProductsConfig: this.sponsoredProductsConfig,
      status: this.status
    }
  }

  setStatus(value: Status) {
    this.status = value
  }

  get isRetailMediaEnabled() {
    return this.rootStore.globalStore.customSitePreferences?.retailMediaEnabled as boolean
  }

  /**
   * Fetch the Sponsored Products configuration from SFCC via API handler
   */
  *getSponsoredProductsConfig() {
    if (this.isRetailMediaEnabled) {
      this.setStatus(StatusMap.PENDING)
      try {
        const res: Response = yield fetch(`${getAppOrigin()}/api/getSponsoredProductsConfig`)
        const json: Record<string, SponsoredProductsConfig> = yield res.json()
        // Set the config in the store
        this.setSponsoredProductsConfig(json)
        this.setStatus(StatusMap.DONE)
      } catch (error) {
        this.setStatus(StatusMap.ERROR)
        throw error
      }
    }
  }

    /**
   * Sets the Sponsored Products Config in the store
   * @param config - the json response from SFCC
   */
  setSponsoredProductsConfig(config: Record<string, SponsoredProductsConfig>) {
    this.sponsoredProductsConfig = config
    
    if (this.sponsoredProductsConfig && Object.keys(this.sponsoredProductsConfig).length > 0) {
      // Set the correct config for search and category pages
      this.searchPageSponsoredProducts = this.getSearchPageSponsoredProducts()
      this.categoryPageSponsoredProducts = this.getCategoryPageSponsoredProducts()
    }
  }

  /**
   * Gets the Sponsored Products Config for Search Pages
   * @returns the Sponsored Products Config for Search Pages, or null if not found
   */
  getSearchPageSponsoredProducts() {
    if (!this.sponsoredProductsConfig) {
      this.searchPageSponsoredProducts = null
      return null
    }
    
    for (const key in this.sponsoredProductsConfig) {
      if (this.sponsoredProductsConfig[key]?.pageType?.toLowerCase() === 'search') {
        this.searchPageSponsoredProducts = this.sponsoredProductsConfig[key]
        return this.searchPageSponsoredProducts
      }
    }
    
    this.searchPageSponsoredProducts = null
    return null
  }

  /**
   * Gets the Sponsored Products Config for Category Pages
   * @returns the Sponsored Products Config for Category Pages, or null if not found
   */
  getCategoryPageSponsoredProducts() {
    if (!this.sponsoredProductsConfig) {
      this.categoryPageSponsoredProducts = null
      return null
    }
    
    for (const key in this.sponsoredProductsConfig) {
      if (this.sponsoredProductsConfig[key]?.pageType?.toLowerCase() === 'category') {
        this.categoryPageSponsoredProducts = this.sponsoredProductsConfig[key]
        return this.categoryPageSponsoredProducts
      }
    }
    
    this.categoryPageSponsoredProducts = null
    return null
  }

  /**
   * Gets/Sets the unique ID for the current Retail Media session
   * @returns {string} the session ID
  */
  getOrSetRMSessionId(): string {
    const key = 'rm_session'

    if (this.retailMediaSessionID) {
      return this.retailMediaSessionID
    }

    if (isServer) {
      const serverId = randomUUID()
      this.retailMediaSessionID = serverId
    } else {
      const storedId = sessionStorage.getItem(key)

      // Use stored ID if available, otherwise generate and store a new one
      const sessionId = storedId || crypto.randomUUID()
  
      if (!storedId) {
        sessionStorage.setItem(key, sessionId)
      }

      this.retailMediaSessionID = sessionId
    }

    return this.retailMediaSessionID
  }

  /**
   * Function to fetch retail media content (ads or products)
   * @param {string} pageType The type of the page (e.g., "search", "category")
   * @param {string} filterValue The search query or category page name
   * @param {string} endpoint The API endpoint to use ("banners" or "products")
   * @param {boolean} includeMaxAds Whether to include maxNumberOfAds in the request
   * @returns {RetailMediaResponse | { retailMediaContent: false } | never[]} The fetched retail media content
   */
  *fetchRetailMediaContent(pageType: string, filterValue: string, endpoint: 'banners' | 'products', includeMaxAds = false) {
    if (!this.isRetailMediaEnabled) {
      return []
    }

    // For product ads, ensure we have the sponsored products config
    if (endpoint === 'products' && includeMaxAds && (!this.sponsoredProductsConfig || !this.categoryPageSponsoredProducts)) {
      yield this.getSponsoredProductsConfig()
    }

    // Check the appropriate store based on endpoint
    const existingContent = endpoint === 'banners' 
      ? this.retailMediaContentById[filterValue] 
      : this.retailMediaSponsoredProductsById[filterValue]

    if (existingContent) {
      return existingContent
    }

    // Determine the correct cache to use
    const retailMediaContent = endpoint === 'banners'
      ? this.retailMediaContentById
      : this.retailMediaSponsoredProductsById

    this.setStatus(StatusMap.PENDING)

    let customerId = ''
    let sessionId = ''
    if (this.rootStore.consentStore.hasMarketingConsent()) {
      customerId = this.rootStore?.customerStore?.customerInfo?.customerId || ''
      sessionId = this.getOrSetRMSessionId()
    }
    
    const requestBody: RequestBody = {
      pageType, 
      filterValue, 
      customerId, 
      sessionId,
      maxNumberOfAds: includeMaxAds ? this.getMaxNumberOfAds(pageType) : 1
    }

    const reqOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(requestBody)
    }

    const response = (yield fetch(`${getAppOrigin()}/api/retailMedia/${endpoint}`, reqOptions)) as Response
    const responseJSON = (yield response.json()) as RetailMediaResponse

    if (responseJSON) {
      retailMediaContent[filterValue] = {...responseJSON, retailMediaContent: true}
    } else {
      retailMediaContent[filterValue] = {retailMediaContent: false}
    }

    this.setStatus(StatusMap.DONE)

    return retailMediaContent[filterValue]
  }

  /**
   * Fetch retail media product ads
   * @param {string} pageType The type of the page (e.g., "search", "category")
   * @param {string} filterValue The search query or category page name
   * @returns {RetailMediaResponse | { retailMediaContent: false } | never[]} The fetched retail media product ads
   */
  *fetchProductAds(pageType: string, filterValue: string) {      
    return yield* this.fetchRetailMediaContent(pageType, filterValue, 'products', true)
  }

  /**
   * Fetch retail media banner ads
   * @param {string} pageType The type of the page (e.g., "search", "category")
   * @param {string} filterValue The search query or category page name
   * @returns {RetailMediaResponse | { retailMediaContent: false } | never[]} The fetched retail media banner ads
   */
  *fetchBannerAds(pageType: string, filterValue: string) {      
    return yield* this.fetchRetailMediaContent(pageType, filterValue, 'banners', true)
  }

  /**
   * Get the maximum number of ads for a specific page type
   * @param { string } pageType The type of the page (e.g., "search", "category")
   * @returns { number } The maximum number of ads
   */
  getMaxNumberOfAds(pageType: string) {
    if (!pageType) return 0
    const config = pageType === 'search' ? this.searchPageSponsoredProducts : this.categoryPageSponsoredProducts
    return config?.maxNumberOfProducts || 0
  }

}